import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { Configuration } from 'src/app/models/configurators-2d/applications/kitchen/configuration';
import { BaseAppService } from '../base-app-service';
import { BathroomConfigurationData } from '../../../stores/tunnel/model/configuration-data/bathroom-configuration-data';
import { TunnelDataStore } from '../../../stores/tunnel/tunnel-data.store';
import { Subscription } from 'rxjs';
import {
  BathroomCreateUpdateTunnel,
  BathroomConfiguration,
  BaseBathroomConfigurationDto,
  BathroomTunnelPricingDto,
  BathroomInstallationDto,
  BathroomTunnelConceptionDetailPrice,
  ProductAttribute,
  BathroomProductDto,
  BathroomConfigurationProductDto,
  BathroomThumbnailDto,
} from 'src/app/models/configurators-2d/tunnel/bathroom/bathroom-tunnel-dtos';

import {
  DuplicateTunnelHBMInput,
  GetTunnelByHBMUUIDInput,
  HbmPdfViewModel,
} from 'src/app/models/configurators-2d/tunnel/common-hbm-tunnel-dtos';
import { ApplyDiscountInput } from 'src/app/models/configurators-2d/tunnel/apply-discount-input';
import { ComputeInstallationOptions } from 'src/app/models/configurators-2d/tunnel/compute-installation-options';
import { DeleteTunnelInput } from 'src/app/models/configurators-2d/tunnel/delete-tunnel-input';
import { FundingLegalInformation } from 'src/app/models/configurators-2d/tunnel/funding-legal-information';
import { GetFundingDataInput } from 'src/app/models/configurators-2d/tunnel/get-funding-data-input';
import { GetFundingLegalInformationInput } from 'src/app/models/configurators-2d/tunnel/get-funding-legal-information-input';
import { GetInstallationDataInput } from 'src/app/models/configurators-2d/tunnel/get-installation-data-input';
import { GetPricingInput } from 'src/app/models/configurators-2d/tunnel/get-pricing-input';
import { TunnelUpdateInstallation } from 'src/app/models/configurators-2d/tunnel/tunnel-update-installation';
import { UpdateInstallationInput } from 'src/app/models/configurators-2d/tunnel/update-installation-input';
import { FundingState } from 'src/app/stores/tunnel/model/funding-state';
import { InstallationState } from 'src/app/stores/tunnel/model/installation-state';
import { Price } from 'src/app/stores/tunnel/model/price';
import { PricingState } from 'src/app/stores/tunnel/model/pricing-state';
import { DiscountTypeEnum } from 'src/app/enum/discount-type.enum';
import { Customer } from '../../kingfisher/customer';
import { GetCustomerFidelityInfoNouveauPointInput } from '../../fidelity/fidelity';
import { FidelityService } from '../../fidelity/fidelity.service';
import { ProjectService } from '../../project.service';
import { HBMMedia, HBMMedias } from '../../../models/configurators-2d/applications/kitchen/hbm-media';
import { logError } from '../../helpers/sentry.helper';
import { FundingRehydrationService } from '../funding-rehydration.service';
import { BathroomTunnelData } from 'src/app/models/configurators-2d/applications/bathroom/bathroom-tunnel-data';
import {
  BathroomCompositionDetailDto,
  BathroomCompositionDto,
  CompositionFilters,
} from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-composition-dtos';
import {
  BathroomConstraintDto,
  BathroomFinishingSimpleDto,
  BathroomRangeDto,
  SearchBathroomProductDto,
} from 'src/app/models/configurators-2d/common-bathroom-dtos';
import { environment } from 'src/environments/environment';
import {
  BathroomConfigurationDto,
  BathroomCreateUpdateConfigurationDto,
} from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-configuration-dtos';
import { param } from 'cypress/types/jquery';
import { FeatureSettingsService } from '../../feature-settings/feature-settings.service';
import { isValidBathroomPreviewImage } from '../mobile/bathroom/bathroom-preview-capture.util';

type BathroomUpdateOptions = {
  updateThumbnail?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class BathroomService extends BaseAppService<Configuration> {
  // Services
  private readonly fidelityService = inject(FidelityService);
  private readonly fundingRehydration = inject(FundingRehydrationService);
  private readonly featureSettingsService = inject(FeatureSettingsService);
  private readonly tunnelDataStore = inject(TunnelDataStore);
  private readonly projectService = inject(ProjectService);

  // Properties
  private currentBathroomConfiguration: BathroomConfigurationData;
  private currentInstallation: InstallationState;
  private currentPricing: PricingState;
  private currentFunding: FundingState;
  private currentCustomer: Customer;
  private readonly subscriptions: Subscription[] = [];

  constructor() {
    super(CONFIG_CONTEXT.BATHROOM_V2, Configuration);
    this.subscribeToObservable();
  }

  isInstallationDisabled(): boolean {
    return environment.installationSpecifics.bathroomV2.disableInstallation;
  }

  private map(data: BathroomConfigurationData): Partial<BathroomTunnelData> {
    return {
      ...(data.projectTitle && { title: data.projectTitle }),
      ...(this.customer.userDatas?.id && { customerId: this.customer.userDatas?.id }),
      ...(this.current?.vendor?.sapId && { creatorId: this.current.vendor.sapId }),
      ...(data.products && { products: data.products }),
      ...(data.configurationId && { id: data.configurationId }),
    };
  }

  private subscribeToObservable() {
    this.subscriptions.push(
      this.tunnelDataStore.getConfigurationData().subscribe(configurationData => {
        if (configurationData) {
          this.currentBathroomConfiguration = configurationData.get(
            CONFIG_CONTEXT.BATHROOM_V2
          ) as BathroomConfigurationData;
        }
      }),
      this.tunnelDataStore.getCurrentInstallation().subscribe(async installation => {
        if (installation) {
          this.currentInstallation = installation;
        }
      }),
      this.tunnelDataStore.getCurrentFunding().subscribe(funding => {
        if (funding) {
          this.currentFunding = funding;
        }
      }),
      this.tunnelDataStore.getCurrentPricing().subscribe(pricing => {
        if (pricing) {
          this.currentPricing = pricing;
        }
      }),
      this.tunnelDataStore.getCurrentCustomer().subscribe(customer => {
        if (customer) {
          this.currentCustomer = customer.userData;
        }
      })
    );
  }

  /**
   * Consentement déjà enregistré pour ce projet, si connu. Lit d'abord l'état du tunnel Bathroom
   * ({@link currentBathroomConfiguration}, jamais alimenté par {@link BaseAppService.currentConfiguration}
   * pour ce flux HBM) ; si ce projet vient d'être rouvert et que la valeur n'a pas encore été
   * chargée en mémoire cette session, va la chercher en base et la met en cache.
   */
  protected override async getPersistedContactedByPhone(configId: string): Promise<boolean | undefined> {
    if (this.currentBathroomConfiguration?.bathroomConfigurationId !== configId) {
      return undefined;
    }
    if (this.currentBathroomConfiguration.contactedByPhone !== undefined) {
      return this.currentBathroomConfiguration.contactedByPhone;
    }

    const persisted = await this.fetchContactedByPhone(configId);
    this.currentBathroomConfiguration.contactedByPhone = persisted;
    return persisted;
  }

  /**
   * Mémorise le consentement résolu dans l'état du tunnel Bathroom, pour qu'un prochain envoi
   * dans la même session (ex. après déverrouillage) le réutilise au lieu de rouvrir la pop-up.
   */
  protected override setPersistedContactedByPhone(configId: string, contactedByPhone: boolean): void {
    if (this.currentBathroomConfiguration?.bathroomConfigurationId === configId) {
      this.currentBathroomConfiguration.contactedByPhone = contactedByPhone;
    }
  }

  protected override get currentConfigurationIdForContactedByPhone(): string | undefined {
    return this.currentBathroomConfiguration?.bathroomConfigurationId;
  }

  public async createBathroom(initialData: BathroomConfigurationData, options: BathroomUpdateOptions = {}) {
    const currentConfigurationId = this.currentBathroomConfiguration?.configurationId;
    const currentBathroomConfigurationId = this.currentBathroomConfiguration?.bathroomConfigurationId;

    let resultCreate = null;
    if (currentConfigurationId) {
      await this.updateBathroomConfiguration({
        ...initialData,
        configurationId: currentConfigurationId,
      }, true, options);
    } else {
      const thumbnail = this.getValidThumbnail(initialData.thumbnail);
      if (!thumbnail) {
        throw new Error('La thumbnail de la configuration salle de bain est requise à la création.');
      }

      resultCreate = await this.createBathroomTunnel({
        products: initialData.products,
        title: initialData.projectTitle,
        thumbnail,
        compositionId: initialData.compositionId,
        compositionConfiguration: initialData.compositionConfiguration,
      });
    }

    this.tunnelDataStore.resetCurrentInstallation();
    this.tunnelDataStore.resetCurrentPricing();
    this.tunnelDataStore.resetCurrentFunding();

    this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.BATHROOM_V2, {
      ...initialData,
      configurationId: currentConfigurationId ?? resultCreate.id,
      bathroomConfigurationId: currentBathroomConfigurationId ?? resultCreate.bathroomConfigurationId,
    } as BathroomConfigurationData);
  }

  public async updateBathroomConfiguration(
    data: BathroomConfigurationData,
    withReset: boolean = true,
    options: BathroomUpdateOptions = {}
  ) {
    const hasExistingConfiguration = !!this.currentBathroomConfiguration?.configurationId;
    data = {
      ...this.currentBathroomConfiguration,
      ...data,
    };
    this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.BATHROOM_V2, data);

    const shouldSendComposition = !hasExistingConfiguration || options.updateThumbnail === true;
    const inputRequest: BathroomCreateUpdateTunnel = {
      tunnelConfigurationId: data.configurationId,
      title: data.projectTitle ?? '',
      products: data.products ?? [],
    };
    if (shouldSendComposition) {
      inputRequest.compositionId = data.compositionId;
      inputRequest.compositionConfiguration = data.compositionConfiguration;
    }
    const thumbnail = this.getValidThumbnail(data.thumbnail);
    if (shouldSendComposition && thumbnail) inputRequest.thumbnail = thumbnail;

    if (this.currentBathroomConfiguration.configurationId == null) {
      if (!thumbnail) {
        throw new Error('La thumbnail de la configuration salle de bain est requise à la création.');
      }

      const resultCreate = await this.createBathroomTunnel(inputRequest);

      this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.BATHROOM_V2, {
        configurationId: resultCreate.id,
        bathroomConfigurationId: resultCreate.bathroomConfigurationId,
        projectTitle: data.projectTitle,
        products: data.products,
        thumbnail: data.thumbnail,
        compositionId: data.compositionId,
        compositionName: data.compositionName,
        compositionThumbnailUrl: data.compositionThumbnailUrl,
        compositionConfiguration: data.compositionConfiguration,
      } as BathroomConfigurationData);
    } else {
      await this.updateBathroomTunnel(inputRequest);
    }

    if (withReset) {
      this.tunnelDataStore.resetCurrentInstallation();
      this.tunnelDataStore.resetCurrentPricing();
      this.tunnelDataStore.resetCurrentFunding();
      this.tunnelDataStore.updateCurrentDiscount(DiscountTypeEnum.Discount0);
    }
  }

  public async addProducts(products: BathroomConfigurationProductDto[]): Promise<void> {
    const configuration = this.currentBathroomConfiguration;

    for (const product of products) {
      const configurationProduct = configuration.products.find(p => p.ean === product.ean);

      // si le produit existe déjà, on ajoute la nouvelle quantité à l'ancienne
      if (configurationProduct) {
        configurationProduct.qty += product.qty;
        continue;
      }

      // si elle n'existe pas, on rajoute le produit dans la liste
      configuration.products.push(product);
    }

    this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.BATHROOM_V2, configuration);

    const inputRequest = {
      tunnelConfigurationId: this.currentBathroomConfiguration.configurationId,
      title: this.currentBathroomConfiguration.projectTitle,
      products: this.currentBathroomConfiguration.products,
    };

    await this.updateBathroomTunnel(inputRequest);
  }

  public async getLiteBathroomConfiguration(configId: string): Promise<BaseBathroomConfigurationDto> {
    return await this.getBathroomTunnelByConfigId(configId);
  }

  public async loadBathroomConfigurationFromId(configId: string): Promise<BaseBathroomConfigurationDto> {
    //le GUID est sensible à la casse, il faut le mettre en majuscule
    configId = configId.toUpperCase();
    let result;
    result = this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.BATHROOM_V2, {
      configurationId: null,
      bathroomConfigurationId: null,
      projectTitle: null,
      productList: null,
      products: null,
      thumbnail: null,
      compositionId: null,
      compositionName: null,
      compositionThumbnailUrl: null,
      compositionConfiguration: null,
      isLocked: false,
    } as BathroomConfigurationData);

    try {
      result = await this.getBathroomTunnelByConfigId(configId);
      this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.BATHROOM_V2, {
        configurationId: result?.id,
        bathroomConfigurationId: result?.bathroomConfigurationId,
        projectTitle: result?.title,
        products: result?.products,
        thumbnail: result?.thumbnail,
        compositionId: result?.compositionId,
        compositionName: result?.compositionName,
        compositionThumbnailUrl: result?.compositionThumbnailUrl,
        compositionConfiguration: result?.compositionConfiguration,
        isLocked: result?.isLocked,
      } as BathroomConfigurationData);

      if (result.id != null && result.products?.length > 0) {
        // Chargement : on laisse l'API répondre avec l'âge d'habitation persisté du projet.
        await this.computeInstallation({ usePersistedHouseAge: true });
        await this.loadBathroomPricing(result.id);
        // Filet : `Tunnel/pricing` ne ramène pas toujours les offres persistées.
        await this.fundingRehydration.rehydrateIfNeeded(result.id, this);
        await this.computeFidelity();
      }

      this.current.setHBMProject({
        locked: this.currentBathroomConfiguration.isLocked,
      });
    } catch (error) {}

    return result;
  }

  protected async fidelityEnabled(): Promise<boolean> {
    return this.featureSettingsService.isFidelityEnabled();
  }

  private async computeFidelity() {
    if (!(await this.fidelityEnabled())) return;
    if (!this.currentPricing?.totalWithoutFunding?.actualAmount) return;
    await this.computeFidelityPoint({
      amount: this.currentPricing.totalWithoutFunding?.actualAmount,
      customerId: this.currentCustomer.kfId,
    });
  }

  /**
   * Récupère l'estimation des prestations de pose (SPID) pour le projet courant.
   *
   * `options.usePersistedHouseAge` doit être posé au chargement d'un tunnel existant : voir
   * {@link ComputeInstallationOptions}.
   */
  public async computeInstallation(options: ComputeInstallationOptions = {}) {
    const poseEnabled = await this.featureSettingsService.isPoseEnabled();

    if (!poseEnabled) {
      this.tunnelDataStore.disableCurrentInstallation();
      return;
    }

    try {
      this.tunnelDataStore.setInstallationLoading(true);
      let result = await this.getInstallationData({
        tunnelConfigurationId: this.currentBathroomConfiguration.configurationId,
        houseMoreThan2YearsOld: options.usePersistedHouseAge ? undefined : this.currentInstallation?.more2Years,
      });
      this.tunnelDataStore.resetCurrentPricing();
      this.tunnelDataStore.resetCurrentFunding();

      this.tunnelDataStore.updateCurrentInstallation({
        more2Years: result.houseMoreThan2YearsOld,
        withInstallation: result.withInstallation,
        detailInstallation: JSON.parse(result.installationPrice?.installationDetails),
      });

      this.loadPricingFromInstallation(result);
    } catch (error) {
      logError(error);
    } finally {
      this.tunnelDataStore.setInstallationLoading(false);
    }
  }

  public async updateDiscount(input: ApplyDiscountInput) {
    this.tunnelDataStore.setPricingLoading(true);
    const financementEnabled = await this.featureSettingsService.isFinancementEnabled();
    const discountInput = {
      ...input,
      requiresFinancingRecalculation: financementEnabled && input.requiresFinancingRecalculation,
    };
    let res = await this.applyDiscount(discountInput);
    await this.updateBathroomPricingFromKitchenTunnelPricing(res);
    await this.computeFidelity();
    this.tunnelDataStore.updateCurrentDiscount(discountInput.discountType);
    this.tunnelDataStore.setPricingLoading(false);
  }

  public async updateWithInstallation(withInstallation: boolean) {
    const poseEnabled = await this.featureSettingsService.isPoseEnabled();

    if (!poseEnabled) {
      this.tunnelDataStore.disableCurrentInstallation();
      return;
    }

    await this.updateInstallation({
      tunnelConfigurationId: this.currentBathroomConfiguration.configurationId,
      installationUpdate: {
        withInstallation: withInstallation,
      },
    });
  }

  public async deleteBathroom(configurationId: string) {
    return await this.deleteConfiguration(configurationId);
  }

  public async loadBathroomPricing(tunnelConfigurationId: string) {
    try {
      await this.computeBathroomPricing({
        tunnelConfigurationId: tunnelConfigurationId,
        shouldRecalculate: false,
      });
    } catch (error) {
      logError(error);
    }
  }

  public async computeFunding(input: GetFundingDataInput) {
    const financementEnabled = await this.featureSettingsService.isFinancementEnabled();

    if (!financementEnabled) {
      // Financement coupé : on recharge quand même le pricing pour que les totaux suivent
      // le scénario de pose courant (le mapper de pricing repasse par disableCurrentFunding).
      this.tunnelDataStore.disableCurrentFunding();
      await this.loadBathroomPricing(input.tunnelConfigurationId);
      return;
    }

    try {
      this.tunnelDataStore.setFundingLoading(true);
      let result = await this.getFundingData(input);
      await this.updatePricing(result);
    } catch (error) {
      logError(error);
    } finally {
      this.tunnelDataStore.setFundingLoading(false);
    }
  }

  public async selectOffer(code: string, duration: number, other: boolean = false) {
    const financementEnabled = await this.featureSettingsService.isFinancementEnabled();

    if (!financementEnabled) {
      this.tunnelDataStore.disableCurrentFunding();
      return;
    }

    await this.getFundingLegalInformation({
      tunnelConfigurationId: this.currentBathroomConfiguration.configurationId,
      selectedBareme: code,
      financingDuration: duration,
    });
    await this.loadBathroomPricing(this.currentBathroomConfiguration.configurationId);
  }

  private async updatePricing(pricing?: BathroomTunnelPricingDto) {
    if (pricing) {
      await this.updateBathroomPricingFromKitchenTunnelPricing(pricing);
    }
  }

  private getFunding(other: boolean) {
    const shouldUseFundingWithoutInstallation = other
      ? this.currentInstallation.withInstallation
      : !this.currentInstallation.withInstallation;
    return this.currentFunding[
      shouldUseFundingWithoutInstallation ? 'fundingWithoutInstallation' : 'fundingWithInstallation'
    ];
  }

  private async computeBathroomPricing(input: GetPricingInput) {
    try {
      let result: BathroomTunnelPricingDto = await this.getPricing(input);
      this.tunnelDataStore.setPricingLoading(true);
      await this.updateBathroomPricingFromKitchenTunnelPricing(result);
    } catch (error) {
      logError(error);
    } finally {
      this.tunnelDataStore.setPricingLoading(false);
    }
  }

  private getFundingLegalInformation(input: GetFundingLegalInformationInput): Promise<FundingLegalInformation> {
    return this.getHbm<FundingLegalInformation>(
      this.apiUrl + 'Tunnel/financing/legal-information/' + input.tunnelConfigurationId,
      input
    );
  }

  private loadPricingFromInstallation(input: BathroomInstallationDto) {
    let withInstallationKey: string = input.withInstallation ? 'withInstallation' : 'withoutInstallation';
    this.tunnelDataStore.updateCurrentPricing({
      totalConception: this.toPrice(
        (input.conceptionPrices[withInstallationKey] as BathroomTunnelConceptionDetailPrice).totalPrice
      ),
      delivery: this.toPrice(input.deliveryPrice),
      totalInstallation: this.toPrice(input.installationPrice.price),
      totalWithoutFunding: this.toPrice(
        (input.conceptionPrices[withInstallationKey] as BathroomTunnelConceptionDetailPrice).totalPrice
      ),
      detailConception: this.getDetailConception(input),

      delta: this.toPrice(input.conceptionPrices.delta),
    });
  }

  private getDetailConception(input: BathroomInstallationDto) {
    if (input.withInstallation) {
      return new Map<string, Price>([
        ['productsPrice', this.toPrice(input.conceptionPrices.withInstallation.productsPrice)],
      ]);
    } else {
      return new Map<string, Price>([
        // Spread les entrées supplémentaires correctement
        ...Object.entries(input.conceptionPrices.categoryTotals).map(([key, value]): [string, Price] => [
          key,
          this.toPrice(value),
        ]),
      ]);
    }
  }

  private async updateBathroomPricingFromKitchenTunnelPricing(input: BathroomTunnelPricingDto) {
    const featureSettings = await this.featureSettingsService.getSettings();

    if (featureSettings.pose) {
      this.updateBathroomInstallation(input);
    } else {
      this.tunnelDataStore.disableCurrentInstallation();
    }

    if (featureSettings.pose && this.currentInstallation?.withInstallation && input.conceptionPrices?.withInstallation) {
      this.computeBathroomPricingWithInstallation(input);
    } else if (input.conceptionPrices?.withoutInstallation) {
      this.computeBathroomPricingWithoutInstallation(input);
    }

    if (featureSettings.financement) {
      await this.loadBathroomFunding(input);
    } else {
      this.tunnelDataStore.disableCurrentFunding();
    }
    if (typeof input.discountEnum === 'number') {
      this.tunnelDataStore.updateCurrentDiscount(this.discountValueToDiscountEnum(input.discountEnum as number));
    } else {
      this.tunnelDataStore.updateCurrentDiscount(input.discountEnum);
    }
  }

  public async computeFidelityPoint(input: GetCustomerFidelityInfoNouveauPointInput) {
    if (!(await this.fidelityEnabled())) return;
    try {
      this.tunnelDataStore.setFidelityLoading(true);

      const [withInstallation, withoutInstallation] = await Promise.all([
        this.fidelityService.getCustomerFidelityCardInfoNouveauPoint(this.buildFidelityWithInstallationInput(input)),
        this.fidelityService.getCustomerFidelityCardInfoNouveauPoint(this.buildFidelityWithoutInstallationInput(input)),
      ]);

      this.tunnelDataStore.updateCurrentPricing({
        ...this.currentPricing,
        fidelity: [withInstallation, withoutInstallation],
      });
    } catch (e) {
      logError(e);
    } finally {
      this.tunnelDataStore.setFidelityLoading(false);
    }
  }

  private buildFidelityWithInstallationInput(
    input: GetCustomerFidelityInfoNouveauPointInput
  ): GetCustomerFidelityInfoNouveauPointInput {
    let otherTotal =
      this.currentFunding?.fundingWithInstallation?.total > 0
        ? this.currentFunding?.fundingWithInstallation?.total
        : this.currentPricing?.totalWithFunding + this.currentPricing?.delta.actualAmount;
    return {
      ...input,
      amount: this.currentInstallation?.withInstallation ? Math.floor(input.amount) : Math.floor(otherTotal),
    };
  }

  private buildFidelityWithoutInstallationInput(
    input: GetCustomerFidelityInfoNouveauPointInput
  ): GetCustomerFidelityInfoNouveauPointInput {
    let otherTotal =
      this.currentFunding?.fundingWithoutInstallation.total > 0
        ? this.currentFunding?.fundingWithoutInstallation?.total
        : this.currentPricing?.totalWithFunding - this.currentPricing?.delta.actualAmount;
    return {
      ...input,
      amount: !this.currentInstallation?.withInstallation ? Math.floor(input.amount) : Math.floor(otherTotal),
    };
  }

  private computeBathroomPricingWithInstallation(result: BathroomTunnelPricingDto) {
    this.baseComputeBathroomPricing(result);
    this.tunnelDataStore.updateCurrentPricing({
      detailConception: new Map<string, Price>([
        ['productsPrice', this.toPrice(result.conceptionPrices?.withInstallation?.productsPrice)],
      ]),
      totalConception: this.toPrice(result.conceptionPrices?.withInstallation?.productsPrice),
    });
  }

  private computeBathroomPricingWithoutInstallation(result: BathroomTunnelPricingDto) {
    this.baseComputeBathroomPricing(result);
    this.tunnelDataStore.updateCurrentPricing({
      detailConception: new Map<string, Price>([
        ['productsPrice', this.toPrice(result.conceptionPrices?.withoutInstallation?.productsPrice)],
      ]),
      totalConception: this.toPrice(result.conceptionPrices?.withoutInstallation?.productsPrice),
    });
  }

  private baseComputeBathroomPricing(result: BathroomTunnelPricingDto) {
    const installationDisabled = !this.featureSettingsService.poseEnabled;
    const totalWithoutFunding = installationDisabled
      ? result.conceptionPrices?.withoutInstallation?.totalPrice ??
        result.conceptionPrices?.withoutInstallation?.productsPrice ??
        result.totalWithoutFinancing
      : result.totalWithoutFinancing;

    this.tunnelDataStore.updateCurrentPricing({
      delivery: this.toPrice(result.deliveryPrice),
      totalInstallation: installationDisabled ? this.toPrice(0) : this.toPrice(result.installationPrices?.price),
      totalWithoutFunding: this.toPrice(totalWithoutFunding),
      delta: installationDisabled ? this.toPrice(0) : this.toPrice(result.conceptionPrices?.delta),
      totalWithFunding: result.totalWithFinancing,
    });
  }

  private updateBathroomInstallation(result: BathroomTunnelPricingDto) {
    let detailInstallation = null;
    if (result.installationPrices?.installationDetails != undefined) {
      detailInstallation = JSON.parse(result.installationPrices?.installationDetails);
    }

    this.tunnelDataStore.updateCurrentInstallation({
      more2Years: result.houseMoreThan2YearsOld,
      withInstallation: result.withInstallation,
      detailInstallation: detailInstallation,
    });
  }

  private async loadBathroomFunding(result: BathroomTunnelPricingDto) {
    this.tunnelDataStore.updateCurrentFunding({
      ...this.mapBathroomTunnelPricingFundingState(result),
    });
  }

  private mapBathroomTunnelPricingFundingState(input: BathroomTunnelPricingDto): FundingState {
    let fundingWithInstallation = this.buildEmptyFunding();
    let fundingWithoutInstallation = this.buildEmptyFunding();

    if (input.financing !== undefined) {
      fundingWithoutInstallation = {
        code: input.financing.select_bareme,
        duration: input.financing.select_duration,
        offerTable: this.mapRawOffersLiteOfferResults(JSON.parse(input.financing.offers)),
        ...this.getDataFromFundingOfferTable(
          input.financing.select_bareme,
          input.financing.select_duration,
          this.mapRawOffersLiteOfferResults(JSON.parse(input.financing.offers))
        ),
      };
    }

    if (input.financingWithInstallation !== undefined) {
      fundingWithInstallation = {
        code: input.financingWithInstallation.select_bareme,
        duration: input.financingWithInstallation.select_duration,
        offerTable: this.mapRawOffersLiteOfferResults(JSON.parse(input.financingWithInstallation.offers)),
        ...this.getDataFromFundingOfferTable(
          input.financingWithInstallation.select_bareme,
          input.financingWithInstallation.select_duration,
          this.mapRawOffersLiteOfferResults(JSON.parse(input.financingWithInstallation.offers))
        ),
      };
    }
    return {
      downPayment: input.downPayment,
      fundingWithInstallation: fundingWithInstallation,
      fundingWithoutInstallation: fundingWithoutInstallation,
    };
  }

  private getInstallationData(input: GetInstallationDataInput): Promise<BathroomInstallationDto> {
    return this.postHbm<{ houseMoreThan2YearsOld: boolean }, BathroomInstallationDto>(
      this.apiUrl + 'Tunnel/installation/' + input.tunnelConfigurationId,
      undefined,
      { houseMoreThan2YearsOld: input.houseMoreThan2YearsOld }
    );
  }

  private getFundingData(input: GetFundingDataInput): Promise<BathroomTunnelPricingDto> {
    return this.postHbm<GetFundingDataInput, BathroomTunnelPricingDto>(
      this.apiUrl + 'Tunnel/financing/' + input.tunnelConfigurationId,
      undefined,
      input
    );
  }

  //quand tu changes le withInstallation
  private updateInstallation(input: UpdateInstallationInput): Promise<void> {
    return this.putHbm<TunnelUpdateInstallation, void>(
      this.apiUrl + 'Tunnel/installation/' + input.tunnelConfigurationId,
      input.installationUpdate
    );
  }

  //quand tu changes le discount
  private applyDiscount(input: ApplyDiscountInput): Promise<BathroomTunnelPricingDto> {
    return this.postHbm<ApplyDiscountInput, BathroomTunnelPricingDto>(
      this.apiUrl + 'Tunnel/discount/' + input.tunnelConfigurationId,
      undefined,
      input
    );
  }

  private getBathroomTunnelByConfigId(configId: string): Promise<BaseBathroomConfigurationDto> {
    return this.get<BaseBathroomConfigurationDto>(this.apiUrl + 'Tunnel/' + configId);
  }

  private getBathroomConfigurationById(id: string): Promise<BaseBathroomConfigurationDto> {
    const params = new HttpParams().set('configurationId', id);
    return this.post<BaseBathroomConfigurationDto>(this.apiUrl + 'Configuration', undefined, params);
  }

  private getPricing(input: GetPricingInput): Promise<BathroomTunnelPricingDto> {
    return this.postHbm<GetPricingInput, BathroomTunnelPricingDto>(
      this.apiUrl + 'Tunnel/pricing/' + input.tunnelConfigurationId,
      undefined,
      input
    );
  }

  private deleteTunnel(input: DeleteTunnelInput): Promise<BaseBathroomConfigurationDto> {
    return this.deleteHbm<DeleteTunnelInput, BaseBathroomConfigurationDto>(
      this.apiUrl + 'Tunnel/delete/' + input.tunnelConfigurationId
    );
  }

  private updateBathroomTunnel(input: BathroomCreateUpdateTunnel): Promise<BathroomConfiguration> {
    return this.putHbm<BathroomCreateUpdateTunnel, BathroomConfiguration>(
      this.apiUrl + 'Tunnel/update/' + input.tunnelConfigurationId,
      input
    );
  }

  private duplicateHbmTunnel(input: DuplicateTunnelHBMInput): Promise<BaseBathroomConfigurationDto> {
    return this.postHbm<DuplicateTunnelHBMInput, BaseBathroomConfigurationDto>(
      this.apiUrl + 'Tunnel/duplicate/hbm',
      undefined,
      input
    );
  }

  private createBathroomTunnel(input: BathroomCreateUpdateTunnel): Promise<BathroomConfiguration> {
    return this.postHbm<BathroomCreateUpdateTunnel, BathroomConfiguration>(this.apiUrl + 'Tunnel/create', input);
  }

  private getValidThumbnail(thumbnail?: BathroomThumbnailDto): BathroomThumbnailDto | undefined {
    return isValidBathroomPreviewImage(thumbnail?.data) ? thumbnail : undefined;
  }

  public async generateBathroomPdf(input: HbmPdfViewModel) {
    return await this.getBathroomPdf(input);
  }

  public async lockUnlockBathroomConfiguration(): Promise<void> {
    try {
      await this.lockUnlockConfig(this.currentBathroomConfiguration.bathroomConfigurationId);
      this.currentBathroomConfiguration.isLocked = !this.currentBathroomConfiguration.isLocked;
      this.current.HBMProject.locked = this.currentBathroomConfiguration.isLocked;
    } catch (error) {}
  }

  public async getBathroomMedias(HBMConfigurationId: string): Promise<HBMMedias> {
    let bathroomMedias: HBMMedias = new HBMMedias();
    try {
      let projectData: any = await this.projectService.getDetails(HBMConfigurationId);
      projectData.medias.thumbnail.map(thumbnail => {
        let p: HBMMedia = {
          name: thumbnail.name,
          url: thumbnail.url,
        };
        bathroomMedias.thumbnail.push(p);
      });

      projectData.medias.plan2D.map(plan2D => {
        let p: HBMMedia = {
          name: plan2D.name,
          url: plan2D.url,
        };
        bathroomMedias.plan2D.push(p);
      });
    } catch (error) {
      logError(error);
    }
    return bathroomMedias;
  }

  private getBathroomPdf(input: HbmPdfViewModel) {
    return this.postHbm<HbmPdfViewModel, any>(this.baseApiUrl + 'Pdf/GeneratePdf/bathroom/', input, {
      tunnelConfigurationId: input.tunnelConfigurationId,
    });
  }

  postConfiguration(input: any): Promise<any> {
    return this.post(this.apiUrl + 'configuration', input);
  }

  getConfigurationByProjectUUID(input: any): Promise<any> {
    return this.post(this.apiUrl + 'configuration/project', input);
  }

  override duplicateProjectById(id: string, title: string): Promise<any> {
    const params = new HttpParams().set('id', id).set('title', title);
    return this.post(`${this.apiUrl}configuration/duplicate`, {}, params);
  }

  async postOffres(input: any): Promise<any> {
    if (!(await this.featureSettingsService.isFinancementEnabled())) {
      this.tunnelDataStore.disableCurrentFunding();
      return undefined;
    }

    try {
      const res = await this.post(this.apiUrl + 'financement/offres', input);
      return res.data;
    } catch {
      return undefined;
    }
  }

  async postMentionsLegale(input: any): Promise<any> {
    if (!(await this.featureSettingsService.isFinancementEnabled())) {
      this.tunnelDataStore.disableCurrentFunding();
      return undefined;
    }

    try {
      const res = await this.post(this.apiUrl + 'financement/mentionslegales', input);
      return res.data;
    } catch {
      return undefined;
    }
  }

  async lockUnlockConfig(configurationId: string): Promise<void> {
    const params = new HttpParams().set('configurationId', configurationId);
    return this.post(this.apiUrl + 'Configuration/LockUnlock', undefined, params);
  }

  /**
   * Récupère toutes les gammes de salle de bain
   */
  public async getAllRange(): Promise<BathroomRangeDto[]> {
    try {
      return await this.get<BathroomRangeDto[]>(`${this.apiUrl}Configuration/GetAllRange`);
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  /**
   * Récupère toutes les compositions pour une gamme donnée
   * @param rangeId - ID de la gamme
   * @returns Liste des compositions
   */
  public async getCompositionsByRangeId(
    rangeId: string,
    filters?: CompositionFilters
  ): Promise<BathroomCompositionDto[]> {
    try {
      return await this.post<BathroomCompositionDto[]>(
        `${this.apiUrl}Configuration/GetCompositionsByRange/${rangeId}`,
        filters ?? null
      );
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  /**
   * Récupère une composition avec tous ses détails (configuration, rows, columns, produits)
   * @param compositionId - ID de la composition
   * @returns Composition avec détails complets
   */
  public async getCompositionById(compositionId: string): Promise<BathroomCompositionDetailDto> {
    try {
      return await this.get<BathroomCompositionDetailDto>(
        `${this.apiUrl}Configuration/GetComposition/${compositionId}`
      );
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  /**
   * Récupère les finitions par attribut de produit
   * @param productAttribute - Attribut de produit
   * @returns Liste des finitions correspondant à l'attribut
   */
  public async getFinishingsByAttribute(productAttribute: ProductAttribute): Promise<BathroomFinishingSimpleDto[]> {
    try {
      return await this.get<BathroomFinishingSimpleDto[]>(
        `${this.apiUrl}Configuration/GetFinishingsByAttribute/${productAttribute}`
      );
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  /**
   * Recherche des produits selon des critères
   * @param searchCriteria - Critères de recherche
   * @returns Liste des produits correspondant aux critères
   */
  public async searchProducts(searchCriteria: SearchBathroomProductDto): Promise<BathroomProductDto[]> {
    try {
      return await this.post<BathroomProductDto[]>(`${this.apiUrl}Configuration/SearchProducts`, searchCriteria);
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  /**
   * Récupère toutes les contraintes
   * @returns Liste de toutes les contraintes
   */
  public async getAllConstraints(): Promise<BathroomConstraintDto[]> {
    try {
      return await this.get<BathroomConstraintDto[]>(`${this.apiUrl}Configuration/GetAllConstraints`);
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  /**
   * Récupère toutes les contraintes pour une gamme spécifique
   * @param rangeId - ID de la gamme
   * @returns Liste des contraintes de la gamme
   */
  public async getConstraintsByRangeId(rangeId: string): Promise<BathroomConstraintDto[]> {
    try {
      return await this.get<BathroomConstraintDto[]>(`${this.apiUrl}Configuration/GetConstraintsByRange/${rangeId}`);
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  /**
   * Création de la configuration en BDD
   */
  public async createConfiguration(config: BathroomCreateUpdateConfigurationDto): Promise<BathroomConfigurationDto> {
    try {
      return await this.post<BathroomConfigurationDto>(`${this.apiUrl}Configuration/Create`, config);
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  /**
   * Suppression de la configuration en BDD
   */
  public async deleteConfiguration(configurationId: string): Promise<void> {
    try {
      const params = new HttpParams().set('configurationId', configurationId);
      await this.post(`${this.apiUrl}Configuration/delete`, undefined, params);
    } catch (error) {
      logError(error);
      throw error;
    }
  }
}
